// Localização: frontend/src/pages/Agendamentos/CalendarView.jsx
// Componente de Calendário Mensal com Drag and Drop para Gestão de Plantões
// Versão 3.0 - Calendário Mensal Simplificado (sem estrutura de horas)

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { 
    Users, Filter, AlertCircle, Clock, User, 
    ChevronLeft, ChevronRight, Calendar as CalendarIcon 
} from 'lucide-react';
import Modal from '../../components/UI/Modal';
import PrimaryButton from '../../components/Common/PrimaryButton';
import { parseLocalDateTime } from '../../utils/formatters';
import EventTooltip from '../../components/Agendamentos/EventTooltip';

// Configurar moment para português
import 'moment/locale/pt-br';
moment.locale('pt-br');

const localizer = momentLocalizer(moment);

// Cores por status
const statusColors = {
    'Pendente': '#fbbf24',      // Amarelo
    'Em Andamento': '#3b82f6',  // Azul
    'Concluído': '#10b981',     // Verde
    'Cancelado': '#ef4444',     // Vermelho
};

// Cores por tipo de agendamento
const tipoColors = {
    'Plantão': '#8b5cf6',       // Roxo
    'Consulta': '#f59e0b',      // Laranja
    'Visita': '#06b6d4',        // Ciano
    'Procedimento': '#ec4899',  // Rosa
};

const CalendarView = ({ 
    agendamentos, 
    pacientes = [],
    profissionais = [],
    onSelectSlot,
    onEventDrop,
    onEventClick,
    canManage = false
}) => {
    // Forçar visualização mensal apenas
    const [view] = useState('month');
    const [date, setDate] = useState(new Date());
    const [selectedProfissional, setSelectedProfissional] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingEvent, setPendingEvent] = useState(null);
    const [eventAction, setEventAction] = useState(null); // 'create', 'move'
    
    // Estado para tooltip
    const [tooltipState, setTooltipState] = useState({
        show: false,
        event: null,
        position: { x: 0, y: 0 }
    });
    const tooltipTimeoutRef = useRef(null);

    // Converter agendamentos para formato do react-big-calendar
    const events = useMemo(() => {
        if (!agendamentos || agendamentos.length === 0) {
            return [];
        }

        let filtered = agendamentos || [];

        // Filtrar por profissional se selecionado
        if (selectedProfissional) {
            filtered = filtered.filter(ag => 
                ag?.usuario_id?.toString() === selectedProfissional
            );
        }

        return filtered.map(agendamento => {
            try {
                if (!agendamento?.data_hora_inicio) {
                    return null;
                }

                // CORREÇÃO: Usar parseLocalDateTime para garantir que a hora seja interpretada corretamente
                const start = parseLocalDateTime(agendamento.data_hora_inicio);
                if (!start || isNaN(start.getTime())) {
                    console.warn('[CalendarView] Data de início inválida:', agendamento.data_hora_inicio);
                    return null;
                }

                // Para visualização mensal, usar o dia inteiro (início e fim do dia)
                // Mantém a hora original para quando precisar salvar
                const startOfDay = moment(start).startOf('day').toDate();
                const endOfDay = agendamento.data_hora_fim 
                    ? moment(parseLocalDateTime(agendamento.data_hora_fim)).endOf('day').toDate()
                    : moment(start).endOf('day').toDate();

                if (!endOfDay || isNaN(endOfDay.getTime())) {
                    const fallbackEnd = moment(start).endOf('day').toDate();
                    console.warn('[CalendarView] Data fim inválida, usando fallback:', fallbackEnd);
                    // Usar o fallback se a data fim for inválida
                    return {
                        id: agendamento.id,
                        title: `${agendamento.paciente_nome || 'Paciente'} - ${agendamento.tipo_cuidado || agendamento.tipo || 'Agendamento'}`,
                        start: startOfDay,
                        end: fallbackEnd,
                        resource: {
                            ...agendamento,
                            color: statusColors[agendamento.status] || tipoColors[agendamento.tipo] || '#6b7280',
                            originalStart: start, // Manter hora original
                            originalEnd: agendamento.data_hora_fim ? parseLocalDateTime(agendamento.data_hora_fim) : null,
                        },
                    };
                }

                // Cor baseada no status, com fallback para tipo
                const statusColor = statusColors[agendamento.status] || '#6b7280';
                const tipoColor = tipoColors[agendamento.tipo] || '#6b7280';
                const color = statusColor !== '#6b7280' ? statusColor : tipoColor;

                return {
                    id: agendamento.id,
                    title: `${agendamento.paciente_nome || 'Paciente'} - ${agendamento.tipo_cuidado || agendamento.tipo || 'Agendamento'}`,
                    start: startOfDay,
                    end: endOfDay,
                    resource: {
                        ...agendamento,
                        color,
                        originalStart: start, // Manter hora original para preservar ao mover
                        originalEnd: agendamento.data_hora_fim ? parseLocalDateTime(agendamento.data_hora_fim) : null,
                    },
                };
            } catch (error) {
                console.error('Erro ao processar agendamento:', error, agendamento);
                return null;
            }
        }).filter(Boolean); // Remove nulls
    }, [agendamentos, selectedProfissional]);

    // Estilizar eventos com melhorias visuais
    const eventStyleGetter = (event) => {
        const color = event.resource?.color || '#6b7280';
        const now = moment();
        const start = moment(event.start);
        const end = moment(event.end);
        
        // Determinar estado do evento
        const isPast = end.isBefore(now);
        const isPresent = start.isBefore(now) && end.isAfter(now);
        const isFuture = start.isAfter(now);
        
        // Classes CSS baseadas no estado
        let className = 'rbc-event';
        if (isPast) className += ' event-past';
        if (isPresent) className += ' event-present';
        if (isFuture) className += ' event-future';

        return {
            className,
            style: {
                backgroundColor: color,
                borderColor: color,
                color: '#fff',
                borderRadius: '4px',
                opacity: isPast ? 0.6 : 1,
                border: isPresent ? '2px solid #10b981' : 'none',
                padding: '2px 5px',
                fontSize: '0.875rem',
                transition: 'all 0.2s ease',
                cursor: canManage ? 'move' : 'pointer',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
            },
        };
    };
    
    // Handlers para tooltip
    const handleEventMouseEnter = useCallback((event, e) => {
        // Limpar timeout anterior se existir
        if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
        }
        
        // Delay para mostrar tooltip (500ms)
        tooltipTimeoutRef.current = setTimeout(() => {
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltipState({
                show: true,
                event: event,
                position: {
                    x: rect.left + rect.width / 2,
                    y: rect.top - 10
                }
            });
        }, 500);
    }, []);
    
    const handleEventMouseLeave = useCallback(() => {
        if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
            tooltipTimeoutRef.current = null;
        }
        setTooltipState(prev => ({ ...prev, show: false }));
    }, []);
    
    // Handler para arrastar evento entre dias (preserva horário original)
    const handleEventDrop = useCallback(({ event, start, end, allDay }) => {
        console.log('[CalendarView] handleEventDrop chamado:', { event, start, end, allDay, canManage });
        
        if (!canManage) {
            console.warn('[CalendarView] Drag bloqueado - canManage é false');
            return;
        }
        
        // Extrair apenas o dia da nova posição, mantendo o horário original
        const originalStart = event.resource?.originalStart || event.start;
        const originalEnd = event.resource?.originalEnd || event.end;
        
        // Obter o novo dia (sem hora)
        const newDay = moment(start).startOf('day');
        
        // Preservar a hora original, apenas mudando o dia
        const newStart = moment(originalStart)
            .year(newDay.year())
            .month(newDay.month())
            .date(newDay.date())
            .toDate();
            
        const newEnd = originalEnd 
            ? moment(originalEnd)
                .year(newDay.year())
                .month(newDay.month())
                .date(newDay.date())
                .toDate()
            : moment(newStart).add(1, 'hour').toDate();

        setPendingEvent({
            ...event.resource,
            start: newStart,
            end: newEnd,
        });
        setEventAction('move');
        setShowConfirmModal(true);
    }, [canManage]);

    // Handler para seleção de slot (criar novo agendamento) - apenas dia, sem hora específica
    const handleSelectSlot = useCallback((slotInfo) => {
        if (!canManage) {
            return;
        }

        // Usar início do dia selecionado, com horário padrão (08:00)
        const selectedDay = moment(slotInfo.start).startOf('day').hour(8).minute(0).toDate();
        const end = moment(selectedDay).add(1, 'hour').toDate();

        setPendingEvent({
            start: selectedDay,
            end,
            paciente_id: '',
            usuario_id: '',
            tipo: 'Visita',
            tipo_cuidado: '',
            descricao: '',
        });
        setEventAction('create');
        setShowConfirmModal(true);
    }, [canManage]);

    // Handler para clicar em evento
    const handleEventClick = useCallback((event) => {
        if (onEventClick) {
            onEventClick(event.resource);
        }
    }, [onEventClick]);

    // Confirmar ação
    const handleConfirm = () => {
        if (eventAction === 'create' && onSelectSlot) {
            onSelectSlot(pendingEvent);
        } else if (eventAction === 'move' && onEventDrop) {
            onEventDrop(pendingEvent);
        }
        setShowConfirmModal(false);
        setPendingEvent(null);
        setEventAction(null);
    };

    // Cancelar ação
    const handleCancel = () => {
        setShowConfirmModal(false);
        setPendingEvent(null);
        setEventAction(null);
    };

    // Navegação do calendário
    const navigate = useCallback((newDate) => {
        setDate(newDate);
    }, []);

    // Estatísticas de plantão
    const plantaoStats = useMemo(() => {
        const hoje = moment().startOf('day');
        const hojeAgendamentos = events.filter(evt => 
            moment(evt.start).isSame(hoje, 'day')
        );

        const porProfissional = {};
        hojeAgendamentos.forEach(evt => {
            const profId = evt.resource?.usuario_id;
            const profNome = evt.resource?.profissional_nome || 'Não atribuído';
            if (!porProfissional[profId]) {
                porProfissional[profId] = {
                    nome: profNome,
                    count: 0,
                    pendentes: 0,
                    emAndamento: 0,
                };
            }
            porProfissional[profId].count++;
            if (evt.resource?.status === 'Pendente') porProfissional[profId].pendentes++;
            if (evt.resource?.status === 'Em Andamento') porProfissional[profId].emAndamento++;
        });

        return {
            totalHoje: hojeAgendamentos.length,
            porProfissional: Object.values(porProfissional),
        };
    }, [events]);

    return (
        <div className="space-y-4">
            {/* Barra de Controles */}
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    {/* Filtro de Profissional */}
                    <div className="flex items-center gap-3 flex-1">
                        <Users className="h-5 w-5 text-gray-500" />
                        <select
                            value={selectedProfissional}
                            onChange={(e) => setSelectedProfissional(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        >
                            <option value="">Todos os Profissionais</option>
                            {profissionais.map(prof => (
                                <option key={prof.id} value={prof.id.toString()}>
                                    {prof.nome_completo} ({prof.role})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Estatísticas de Plantão */}
                    {plantaoStats.totalHoje > 0 && (
                        <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-2 text-emerald-600">
                                <CalendarIcon className="h-4 w-4" />
                                <span className="font-semibold">{plantaoStats.totalHoje} agendamentos hoje</span>
                            </div>
                            {plantaoStats.porProfissional.length > 0 && (
                                <div className="flex items-center gap-2 text-blue-600">
                                    <Users className="h-4 w-4" />
                                    <span>{plantaoStats.porProfissional.length} profissionais</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Navegação do Calendário */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setDate(moment(date).subtract(1, 'month').toDate())}
                            className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                            title="Mês Anterior"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setDate(new Date())}
                            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors font-medium"
                            title="Hoje"
                        >
                            Hoje
                        </button>
                        <button
                            onClick={() => setDate(moment(date).add(1, 'month').toDate())}
                            className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                            title="Próximo Mês"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                        <div className="px-4 py-2 text-gray-700 font-semibold min-w-[180px] text-center">
                            {moment(date).format('MMMM [de] YYYY')}
                        </div>
                    </div>
                </div>

                {/* Lista de Profissionais com Carga de Trabalho */}
                {plantaoStats.porProfissional.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex flex-wrap gap-3">
                            {plantaoStats.porProfissional.map((stat, idx) => (
                                <div 
                                    key={idx}
                                    className={`px-3 py-2 rounded-lg text-xs ${
                                        stat.count > 5 
                                            ? 'bg-red-100 text-red-800 border border-red-300' 
                                            : stat.count > 3
                                            ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                                            : 'bg-green-100 text-green-800 border border-green-300'
                                    }`}
                                >
                                    <div className="font-semibold">{stat.nome}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span>{stat.count} agendamentos</span>
                                        {stat.pendentes > 0 && (
                                            <span className="text-yellow-600">• {stat.pendentes} pendentes</span>
                                        )}
                                        {stat.emAndamento > 0 && (
                                            <span className="text-blue-600">• {stat.emAndamento} em andamento</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Calendário */}
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200" style={{ height: '600px', position: 'relative' }}>
                {events.length === 0 && agendamentos.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-500">
                        <div className="text-center">
                            <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                            <p className="text-lg font-semibold">Nenhum agendamento encontrado</p>
                            <p className="text-sm mt-2">Crie um novo agendamento para visualizá-lo no calendário</p>
                        </div>
                    </div>
                ) : (
                    <Calendar
                        localizer={localizer}
                        events={events}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: '100%' }}
                        view={view}
                        date={date}
                        onNavigate={navigate}
                        onSelectSlot={handleSelectSlot}
                        onEventDrop={handleEventDrop}
                        onSelectEvent={handleEventClick}
                        selectable={canManage}
                        resizable={false}
                        draggableAccessor={(event) => canManage}
                        eventPropGetter={eventStyleGetter}
                        messages={{
                            next: 'Próximo',
                            previous: 'Anterior',
                            today: 'Hoje',
                            month: 'Mês',
                            date: 'Data',
                            event: 'Evento',
                            noEventsInRange: 'Não há agendamentos neste período.',
                        }}
                        formats={{
                            dayHeaderFormat: 'dddd, DD/MM',
                            dayFormat: 'DD',
                            weekdayFormat: 'ddd',
                            monthHeaderFormat: 'MMMM [de] YYYY',
                        }}
                        popup={true}
                        popupOffset={30}
                    />
                )}
            </div>

            {/* Modal de Confirmação */}
            <Modal
                isOpen={showConfirmModal}
                onClose={handleCancel}
                title={
                    eventAction === 'create' ? 'Criar Novo Agendamento' :
                    'Mover Agendamento'
                }
            >
                <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-blue-900 mb-1">
                                    {eventAction === 'create' && 'Confirmar criação de agendamento?'}
                                    {eventAction === 'move' && 'Confirmar movimentação do agendamento para outro dia?'}
                                </h4>
                                <p className="text-sm text-blue-700">
                                    {pendingEvent && (
                                        <>
                                            <strong>Data:</strong> {moment(pendingEvent.start).format('DD/MM/YYYY')}
                                            {pendingEvent.start && (
                                                <> às {moment(pendingEvent.start).format('HH:mm')}</>
                                            )}
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>

                    {eventAction === 'create' && (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Paciente *
                                </label>
                                <select
                                    value={pendingEvent?.paciente_id || ''}
                                    onChange={(e) => setPendingEvent({
                                        ...pendingEvent,
                                        paciente_id: e.target.value
                                    })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                    required
                                >
                                    <option value="">Selecione o paciente</option>
                                    {pacientes.map(paciente => (
                                        <option key={paciente.id} value={paciente.id}>
                                            {paciente.nome_completo}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Profissional
                                </label>
                                <select
                                    value={pendingEvent?.usuario_id || ''}
                                    onChange={(e) => setPendingEvent({
                                        ...pendingEvent,
                                        usuario_id: e.target.value
                                    })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                >
                                    <option value="">Não atribuído</option>
                                    {profissionais.map(prof => (
                                        <option key={prof.id} value={prof.id}>
                                            {prof.nome_completo} ({prof.role})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Tipo de Cuidado
                                </label>
                                <input
                                    type="text"
                                    value={pendingEvent?.tipo_cuidado || ''}
                                    onChange={(e) => setPendingEvent({
                                        ...pendingEvent,
                                        tipo_cuidado: e.target.value
                                    })}
                                    placeholder="Ex: Curativo, Medição de Glicemia"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            onClick={handleCancel}
                            className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <PrimaryButton
                            onClick={handleConfirm}
                            disabled={eventAction === 'create' && !pendingEvent?.paciente_id}
                        >
                            {eventAction === 'create' ? 'Criar Agendamento' : 'Confirmar'}
                        </PrimaryButton>
                    </div>
                </div>
            </Modal>
            
            {/* Tooltip de Evento */}
            {tooltipState.show && tooltipState.event && (
                <EventTooltip
                    event={tooltipState.event}
                    position={tooltipState.position}
                    onEdit={(event) => {
                        setTooltipState({ ...tooltipState, show: false });
                        if (onEventClick) {
                            onEventClick(event.resource);
                        }
                    }}
                    onDelete={(eventId) => {
                        setTooltipState({ ...tooltipState, show: false });
                        // Ação de deletar pode ser adicionada aqui se necessário
                    }}
                />
            )}
        </div>
    );
};

export default CalendarView;

